import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';

// APEX AI Tool 1 — Digital Footprint & Bio Optimizer.
// Takes a raw platform bio + athlete facts, returns an audit + optimized bio,
// 3-post pinned structure, and a media-kit paragraph.
export default function ApexBioOptimizer() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('instagram');
  const [currentBio, setCurrentBio] = useState('');
  const [gradYear, setGradYear] = useState('');
  const [position, setPosition] = useState('');
  const [school, setSchool] = useState('');
  const [keyStat, setKeyStat] = useState('');
  const [tone, setTone] = useState('confident');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    setErr(''); setResult(null); setLoading(true);
    try {
      const r = await api.post('/apex/tools/bio-optimizer', {
        platform,
        current_bio: currentBio,
        grad_year: gradYear ? Number(gradYear) : null,
        position: position || null,
        school: school || null,
        key_stat: keyStat || null,
        tone,
      });
      setResult(r.data.result);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'AI service is unavailable — try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-bio-optimizer-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button onClick={() => navigate('/apex/course')}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-2 mb-6"
                data-testid="apex-bio-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to A.P.E.X.
        </button>

        <div className="flex items-start gap-3 mb-2">
          <Sparkles className="w-6 h-6 text-orange-400 mt-1" />
          <div>
            <div className="text-xs text-orange-500 uppercase tracking-widest mb-1">Module 1 · AI Tool</div>
            <h1 className="text-3xl font-black">Digital Footprint & Bio Optimizer</h1>
          </div>
        </div>
        <p className="text-sm text-gray-400 mb-6">
          Paste your current bio, add a few facts, and Coach B's AI will return an optimized bio, a 3-post pinned strategy, and a media-kit paragraph a college coach can paste into a recruiting board.
        </p>

        <form onSubmit={run} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">Platform</label>
              <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                      className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-white text-sm"
                      data-testid="apex-bio-platform">
                <option value="instagram">Instagram</option>
                <option value="x">X / Twitter</option>
                <option value="hudl">Hudl</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)}
                      className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-white text-sm"
                      data-testid="apex-bio-tone">
                <option value="confident">Confident coach</option>
                <option value="warm">Warm teacher</option>
                <option value="broadcast">Broadcast anchor</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">Current Bio</label>
            <Textarea value={currentBio} onChange={(e) => setCurrentBio(e.target.value)} required
              placeholder="Paste your existing Instagram/X/Hudl bio here…"
              className="bg-black border-gray-700 text-white min-h-[80px]"
              data-testid="apex-bio-current" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Class of (e.g. 2028)" value={gradYear} onChange={(e) => setGradYear(e.target.value)}
                   className="bg-black border-gray-700 text-white" data-testid="apex-bio-year" />
            <Input placeholder="Position (e.g. PG, QB, MF)" value={position} onChange={(e) => setPosition(e.target.value)}
                   className="bg-black border-gray-700 text-white" data-testid="apex-bio-pos" />
            <Input placeholder="High school & city" value={school} onChange={(e) => setSchool(e.target.value)}
                   className="bg-black border-gray-700 text-white" data-testid="apex-bio-school" />
            <Input placeholder='Key stat (e.g. "3.8 GPA · 21ppg")' value={keyStat} onChange={(e) => setKeyStat(e.target.value)}
                   className="bg-black border-gray-700 text-white" data-testid="apex-bio-stat" />
          </div>

          {err && (
            <div className="flex items-start gap-2 p-3 rounded border border-red-500/40 bg-red-500/5" data-testid="apex-bio-error">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-300">{err}</p>
            </div>
          )}

          <Button type="submit" disabled={loading || !currentBio.trim()}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold h-11"
                  data-testid="apex-bio-run-btn">
            {loading ? 'Coach B is thinking…' : 'Optimize My Bio →'}
          </Button>
        </form>

        {result && (
          <div className="mt-8 space-y-4" data-testid="apex-bio-result">
            <Card className="bg-[#111] border-orange-500/30">
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-xs text-orange-400 uppercase tracking-widest font-bold">Audit</div>
                  <div className="text-2xl font-black text-orange-400" data-testid="apex-bio-score">{result.audit?.score_out_of_10}/10</div>
                </div>
                {result.audit?.top_issues?.length > 0 && (
                  <>
                    <div className="text-xs text-red-400 mt-3 mb-1">Top issues:</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      {result.audit.top_issues.map((x, i) => <li key={i} className="flex gap-2"><span className="text-red-400">·</span>{x}</li>)}
                    </ul>
                  </>
                )}
                {result.audit?.what_works?.length > 0 && (
                  <>
                    <div className="text-xs text-green-400 mt-3 mb-1">What works:</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      {result.audit.what_works.map((x, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="w-3 h-3 text-green-400 mt-1 flex-shrink-0" />{x}</li>)}
                    </ul>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#111] border-green-500/30">
              <CardContent className="p-5">
                <div className="text-xs text-green-400 uppercase tracking-widest font-bold mb-2">Optimized Bio</div>
                <p className="text-white font-mono text-sm p-3 bg-black rounded border border-green-500/20" data-testid="apex-bio-optimized">{result.optimized_bio}</p>
              </CardContent>
            </Card>

            {result.pinned_posts?.length > 0 && (
              <Card className="bg-[#111] border-gray-800/60">
                <CardContent className="p-5">
                  <div className="text-xs text-orange-400 uppercase tracking-widest font-bold mb-3">3-Post Pinned Strategy</div>
                  <div className="space-y-3">
                    {result.pinned_posts.map((p, i) => (
                      <div key={i} className="p-3 bg-black rounded border border-gray-800">
                        <div className="text-xs text-orange-300 mb-1">Slot {p.slot} · {p.purpose}</div>
                        <p className="text-sm text-gray-200 mb-1">{p.caption_template}</p>
                        {p.cta && <p className="text-xs text-gray-500">CTA: {p.cta}</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {result.media_kit_paragraph && (
              <Card className="bg-[#111] border-gray-800/60">
                <CardContent className="p-5">
                  <div className="text-xs text-orange-400 uppercase tracking-widest font-bold mb-2">Media Kit Paragraph</div>
                  <p className="text-sm text-gray-200 leading-relaxed">{result.media_kit_paragraph}</p>
                </CardContent>
              </Card>
            )}

            {result.warnings?.length > 0 && (
              <Card className="bg-red-500/5 border-red-500/40">
                <CardContent className="p-5">
                  <div className="text-xs text-red-400 uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Warnings
                  </div>
                  <ul className="text-sm text-red-200 space-y-1">
                    {result.warnings.map((w, i) => <li key={i}>· {w}</li>)}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
